import { useCallback } from 'react'
import type { JSX } from 'react/jsx-runtime'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/features/auth/hooks/use-auth'
import { useChamber } from '@/hooks/use-chamber'
import { supabase } from '@/lib/supabase'
import type {
  ClientInsert,
  ClientRow,
  ClientsProviderProps,
  UseClientsReturn,
  ViewClientStats,
} from '@/types'
import { clientsContext } from './clients-context'

export const ClientsProvider = ({
  children,
}: ClientsProviderProps): JSX.Element => {
  const { user } = useAuth()
  const { chamber } = useChamber()
  const queryClient = useQueryClient()

  // Fetch clients query
  const {
    data: clients = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['clients', chamber?.id],
    queryFn: async (): Promise<ViewClientStats[]> => {
      if (!user) throw new Error('Authentication required')

      const { data, error } = await supabase
        .from('view_client_stats')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error

      return data
    },
    enabled: Boolean(user),
  })

  // Create client mutation
  const createClientMutation = useMutation({
    mutationFn: async (clientData: ClientInsert): Promise<ClientRow> => {
      if (!user) throw new Error('Not authenticated')
      if (!chamber?.id)
        throw new Error(
          'No chamber found. Please ensure you are part of a chamber.',
        )

      const clientDataWithIds: ClientInsert = {
        ...clientData,
        chamber_id: chamber.id,
        user_id: user.id,
      }

      const options = clientData.email ? { onConflict: 'chamber_id,email' } : {}
      const { data, error } = await supabase
        .from('clients')
        .upsert(clientDataWithIds, options)
        .select()
        .single()

      if (error) throw new Error(error.message)

      return data
    },
    onSuccess: () => {
      // Invalidate and refetch clients
      void queryClient.invalidateQueries({ queryKey: ['clients', chamber?.id] })
    },
  })

  // Wrapper functions
  const createClient = useCallback(
    async (clientData: ClientInsert): Promise<ClientRow> => {
      return createClientMutation.mutateAsync(clientData)
    },
    [createClientMutation],
  )

  const refetchClients = useCallback(async (): Promise<undefined> => {
    await refetch()
  }, [refetch])

  const value: UseClientsReturn = {
    clients,
    createClient,
    isLoading,
    refetch: refetchClients,
  }

  return (
    <clientsContext.Provider value={value}>{children}</clientsContext.Provider>
  )
}
